import {Box} from "@mui/material";
import type {SxProps, Theme, TypographyProps} from "@mui/material";
import {useState} from "react";
import {ClickToEdit} from "~/components/ClickToEdit/ClickToEdit";
import {notificationStore} from "~/stores/init";
import {useUpdateSupplierEntityCanonicalNameMutation} from "../hooks/useUpdateSupplierEntityCanonicalNameMutation";

type SupplierCanonicalNameEditorProps = {
    entityId: number;
    canonicalName: string;
    typographyProps?: TypographyProps;
    inputSx?: SxProps<Theme>;
};

/** Editable canonical name — shared by the registry cell and the detail page heading. */
export function SupplierCanonicalNameEditor({
                                                entityId,
                                                canonicalName,
                                                typographyProps,
                                                inputSx,
                                            }: SupplierCanonicalNameEditorProps) {
    const [displayName, setDisplayName] = useState(canonicalName);
    const [prevName, setPrevName] = useState(canonicalName);
    if (prevName !== canonicalName) {
        setPrevName(canonicalName);
        setDisplayName(canonicalName);
    }

    const {mutate} = useUpdateSupplierEntityCanonicalNameMutation();

    const onSave = (value: string) => {
        const trimmed = value.trim();
        if (!trimmed || trimmed === displayName) return;

        const previous = displayName;
        setDisplayName(trimmed);
        mutate(
            {entityId, canonical_name: trimmed},
            {
                onError: () => {
                    setDisplayName(previous);
                    notificationStore.show("Could not rename supplier. Please try again.", {
                        severity: "error",
                        id: `supplier-rename-error-${entityId}`,
                        autoHideDuration: 3000,
                    });
                },
            },
        );
    };

    return (
        <Box sx={{width: '100%', maxWidth: 'max-content', minWidth: 0}}>
            <ClickToEdit
                text={displayName}
                onSave={onSave}
                placeholder="Enter name"
                typographyProps={typographyProps}
                inputSx={inputSx}
            />
        </Box>
    );
}
